import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Search, Send, Users, BookMarked, ShieldCheck, MessageSquare, Ban, RotateCcw } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function StatCard({ label, value, testId }) {
  return (
    <div className="paper-card p-4" data-testid={testId}>
      <div className="overline mb-1">{label}</div>
      <div className="rainbow-text text-3xl">{value ?? "—"}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [uRole, setURole] = useState("all");
  const [uSearch, setUSearch] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [cSearch, setCSearch] = useState("");
  const [contactMsgs, setContactMsgs] = useState([]);
  const [broadcast, setBroadcast] = useState({ target: "all", subject: "", body: "" });
  const [sending, setSending] = useState(false);

  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(apiError(e)));
  const loadUsers = () => api.get("/admin/users", { params: { role: uRole === "all" ? undefined : uRole, search: uSearch || undefined } })
    .then(({ data }) => setUsers(data)).catch((e) => toast.error(apiError(e)));
  const loadCampaigns = () => api.get("/admin/campaigns", { params: { search: cSearch || undefined } })
    .then(({ data }) => setCampaigns(data)).catch((e) => toast.error(apiError(e)));
  const loadContact = () => api.get("/admin/contact-messages").then(({ data }) => setContactMsgs(data)).catch(() => {});

  useEffect(() => { loadStats(); loadContact(); }, []);
  useEffect(() => { loadUsers(); /* eslint-disable-next-line */ }, [uRole, uSearch]);
  useEffect(() => { loadCampaigns(); /* eslint-disable-next-line */ }, [cSearch]);

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.display_name || u.email}? This removes their campaigns, applications, and chats.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User removed"); loadUsers(); loadStats(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const banUser = async (u) => {
    const reason = window.prompt(`Ban ${u.display_name || u.email}? Their profile stays, but they can't log in, message, or apply. Optional reason:`);
    if (reason === null) return;
    try { await api.post(`/admin/users/${u.id}/ban`, { reason }); toast.success("User banned"); loadUsers(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const unbanUser = async (u) => {
    try { await api.post(`/admin/users/${u.id}/unban`, {}); toast.success("User reinstated"); loadUsers(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.title}"? All its applications will be removed too.`)) return;
    try { await api.delete(`/admin/campaigns/${c.id}`); toast.success("Campaign removed"); loadCampaigns(); loadStats(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const sendBroadcast = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send this email to ${broadcast.target === "all" ? "all readers + authors" : `all ${broadcast.target}s`}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", broadcast);
      toast.success(`Queued ${data.queued} email${data.queued === 1 ? "" : "s"}`);
      setBroadcast({ target: broadcast.target, subject: "", body: "" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><ShieldCheck size={12} /> Admin console</div>
          <h1 className="rainbow-text text-4xl tracking-tight">Control room</h1>
          <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>Signed in as <strong>{user?.display_name}</strong> · {user?.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatCard label="Readers" value={stats.readers} testId="stat-readers" />
        <StatCard label="Authors" value={stats.authors} testId="stat-authors" />
        <StatCard label="Active campaigns" value={stats.campaigns_active} testId="stat-campaigns" />
        <StatCard label="Applications" value={stats.applications_total} testId="stat-apps" />
        <StatCard label="Pending apps" value={stats.applications_pending} testId="stat-pending" />
        <StatCard label="Conversations" value={stats.conversations} testId="stat-convos" />
        <StatCard label="Messages" value={stats.messages} testId="stat-messages" />
        <StatCard label="Contact msgs" value={stats.contact_messages} testId="stat-contact" />
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users size={14} className="mr-1.5" />Users</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="admin-tab-campaigns"><BookMarked size={14} className="mr-1.5" />Campaigns</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="admin-tab-broadcast"><Send size={14} className="mr-1.5" />Broadcast</TabsTrigger>
          <TabsTrigger value="contact" data-testid="admin-tab-contact"><MessageSquare size={14} className="mr-1.5" />Contact inbox</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ink-muted)" }} />
              <Input className="pl-9" placeholder="Search by email, display name, or real name…" value={uSearch} onChange={(e) => setUSearch(e.target.value)} data-testid="admin-users-search" />
            </div>
            <Select value={uRole} onValueChange={setURole}>
              <SelectTrigger className="md:w-48" data-testid="admin-users-role"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="reader">Readers</SelectItem>
                <SelectItem value="author">Authors</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="paper-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead>
                  <tr style={{ background: "#0b0b0f", color: "var(--ink-muted)" }}>
                    <th className="text-left py-3 px-4 font-normal">User</th>
                    <th className="text-left py-3 px-4 font-normal">Role</th>
                    <th className="text-left py-3 px-4 font-normal">Joined</th>
                    <th className="text-left py-3 px-4 font-normal">Flags</th>
                    <th className="text-right py-3 px-4 font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={5} className="py-8 text-center" style={{ color: "var(--ink-muted)" }}>No users match.</td></tr>
                  )}
                  {users.map((u) => (
                    <tr key={u.id} className="border-t" style={{ borderColor: "var(--border-soft)" }} data-testid={`admin-user-row-${u.id}`}>
                      <td className="py-3 px-4">
                        <div className="font-bold">{u.display_name || "(no name)"}</div>
                        <div className="text-xs" style={{ color: "var(--ink-muted)" }}>{u.email}</div>
                        {u.real_name && <div className="text-[11px] italic" style={{ color: "var(--ink-muted)" }}>Real: {u.real_name}</div>}
                      </td>
                      <td className="py-3 px-4 capitalize">{u.role || "pending"}</td>
                      <td className="py-3 px-4 text-xs" style={{ color: "var(--ink-muted)" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {u.is_admin && <Badge className="border" style={{ background: "rgba(224,64,251,0.15)", color: "#E040FB", borderColor: "rgba(224,64,251,0.35)" }}>Admin</Badge>}
                          {u.is_banned && <Badge className="border" style={{ background: "rgba(255,59,59,0.15)", color: "#FF3B3B", borderColor: "rgba(255,59,59,0.4)" }} title={u.ban_reason || ""}>Banned</Badge>}
                          {u.google_linked && <Badge variant="outline">Google</Badge>}
                          {u.auto_accept_enabled && <Badge className="status-auto border">Auto-Accept</Badge>}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {u.role === "author" && (
                          <Link to={`/authors/${u.id}`}><Button size="sm" variant="outline" className="btn-press mr-2">View</Button></Link>
                        )}
                        {!u.is_admin && (
                          u.is_banned ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="btn-press mr-2"
                              style={{ borderColor: "rgba(33,197,93,0.4)", color: "#21C55D" }}
                              onClick={() => unbanUser(u)}
                              data-testid={`admin-unban-user-${u.id}`}
                            >
                              <RotateCcw size={14} className="mr-1" /> Unban
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="btn-press mr-2"
                              style={{ borderColor: "rgba(255,212,0,0.4)", color: "#FFD400" }}
                              onClick={() => banUser(u)}
                              data-testid={`admin-ban-user-${u.id}`}
                            >
                              <Ban size={14} className="mr-1" /> Ban
                            </Button>
                          )
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="btn-press"
                          style={{ borderColor: "rgba(255,59,59,0.4)", color: "#FF3B3B" }}
                          disabled={u.is_admin}
                          onClick={() => deleteUser(u)}
                          data-testid={`admin-delete-user-${u.id}`}
                        >
                          <Trash2 size={14} className="mr-1" /> Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="campaigns" className="mt-6">
          <div className="relative mb-4">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ink-muted)" }} />
            <Input className="pl-9" placeholder="Search campaign titles…" value={cSearch} onChange={(e) => setCSearch(e.target.value)} data-testid="admin-campaigns-search" />
          </div>
          <div className="paper-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-campaigns-table">
                <thead>
                  <tr style={{ background: "#0b0b0f", color: "var(--ink-muted)" }}>
                    <th className="text-left py-3 px-4 font-normal">Title</th>
                    <th className="text-left py-3 px-4 font-normal">Author</th>
                    <th className="text-left py-3 px-4 font-normal">Genre</th>
                    <th className="text-left py-3 px-4 font-normal">Format</th>
                    <th className="text-left py-3 px-4 font-normal">Apps</th>
                    <th className="text-right py-3 px-4 font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center" style={{ color: "var(--ink-muted)" }}>No campaigns match.</td></tr>
                  )}
                  {campaigns.map((c) => (
                    <tr key={c.id} className="border-t" style={{ borderColor: "var(--border-soft)" }} data-testid={`admin-campaign-row-${c.id}`}>
                      <td className="py-3 px-4">
                        <Link to={`/campaigns/${c.id}`} className="font-bold hover:underline">{c.title}</Link>
                        <div className="text-xs" style={{ color: "var(--ink-muted)" }}>{new Date(c.created_at).toLocaleDateString()}</div>
                      </td>
                      <td className="py-3 px-4">{c.author_display_name} <span className="text-xs" style={{ color: "var(--ink-muted)" }}>· {c.pen_name}</span></td>
                      <td className="py-3 px-4">{c.genre}</td>
                      <td className="py-3 px-4 capitalize">{c.arc_format}</td>
                      <td className="py-3 px-4">{c.applications_count}</td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="btn-press"
                          style={{ borderColor: "rgba(255,59,59,0.4)", color: "#FF3B3B" }}
                          onClick={() => deleteCampaign(c)}
                          data-testid={`admin-delete-campaign-${c.id}`}
                        >
                          <Trash2 size={14} className="mr-1" /> Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="broadcast" className="mt-6">
          <form onSubmit={sendBroadcast} className="paper-card p-6 space-y-5 max-w-2xl" data-testid="admin-broadcast-form">
            <div>
              <Label>Send to</Label>
              <Select value={broadcast.target} onValueChange={(v) => setBroadcast({ ...broadcast, target: v })}>
                <SelectTrigger data-testid="admin-broadcast-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All readers + authors</SelectItem>
                  <SelectItem value="reader">Readers only</SelectItem>
                  <SelectItem value="author">Authors only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bc-subject">Subject</Label>
              <Input id="bc-subject" required value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })} data-testid="admin-broadcast-subject" />
            </div>
            <div>
              <Label htmlFor="bc-body">Message body</Label>
              <Textarea id="bc-body" rows={8} required minLength={1} value={broadcast.body} onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })} data-testid="admin-broadcast-body" />
              <p className="text-xs mt-1" style={{ color: "var(--ink-muted)" }}>Line breaks preserved. Sent from the site email with the FSH branded template.</p>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={sending} className="btn-press btn-rainbow" data-testid="admin-broadcast-send">
                <Send size={14} className="mr-1.5" /> {sending ? "Queuing…" : "Send broadcast"}
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="contact" className="mt-6">
          <div className="paper-card overflow-hidden" data-testid="admin-contact-list">
            {contactMsgs.length === 0 ? (
              <div className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No contact-form messages yet.</div>
            ) : (
              <ul>
                {contactMsgs.map((m) => (
                  <li key={m.id} className="p-4 border-b" style={{ borderColor: "var(--border-soft)" }} data-testid={`admin-contact-${m.id}`}>
                    <div className="flex justify-between gap-3 mb-1">
                      <div className="font-bold">{m.subject}</div>
                      <div className="text-xs" style={{ color: "var(--ink-muted)" }}>{new Date(m.created_at).toLocaleString()}</div>
                    </div>
                    <div className="text-xs mb-2" style={{ color: "var(--ink-muted)" }}>{m.name} &lt;{m.email}&gt;</div>
                    <div className="text-sm whitespace-pre-wrap">{m.message}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
